import React from 'react';
import classnames from 'classnames';
import * as Dropdown from 'hellospa/foundation/control/dropdown';
import { DropdownField } from 'hellospa/components/editor/types';
import styles from './dropdown.scss';
import textStyles from './text.scss';
import { DEFAULT_FONT_FAMILY, DEFAULT_FONT_SIZE } from '../constants';

// Other field types use a real event, but we don't have one with the dropdown.
// The only thing it really needs is the value
// https://github.com/HelloFax/hellosign-web/blob/edd601fc127b875caed37d2ea643e373b9400137/src/hellospa/components/signer-signature-document/signer-field.jsx#L54-L59
type EventLike = {
  target: {
    value: string
  }
}
export type Props = {
  className?: string,
  fieldData: DropdownField,
  hideControls?: boolean,
  overrideValue?: string,
  onChange: (e: EventLike) => void
}

export const ZERO_WIDTH_SPACE = '\u{200b}';

function findBtn(element: HTMLElement): HTMLElement | null {
  if (element.dataset && element.dataset.btn) {
    return element;
  }

  for (let i = 0; i < element.children.length; i++) {
    const result = findBtn(element.children[i] as HTMLElement);
    if (result) {
      return result;
    }
  }
  return null;
}

function DropdownFieldUI(props: Props, ref: any) {
  const { hideControls = true, fieldData, overrideValue } = props;
  const container = React.useRef<HTMLDivElement>(null);

  const handleSelection = (value: string) => {
    props.onChange({
      target: { value },
    });
  };

  React.useImperativeHandle(ref, () => ({
    measure() {
      if (container.current) {
        return {
          width: container.current.scrollWidth,
          height: container.current.scrollHeight,
        };
      }
      return { width: 0, height: 0 };
    },
    focus() {
      // react-aria-menubutton doesn't provide references to the DOM node, so I
      // have to hold a reference to my own DIV and go find the button element.
      if (container.current) {
        const btn = findBtn(container.current);
        if (btn) {
          btn.focus();
        }
      }
    },
  }), []);


  const fontFamily = fieldData.fontFamily || DEFAULT_FONT_FAMILY;
  const fontSize = fieldData.fontSize || DEFAULT_FONT_SIZE;

  const btnText = overrideValue || fieldData.value || ZERO_WIDTH_SPACE;
  const className = classnames(
    props.className,
    styles.button,
    textStyles[`font--${fontFamily}`],
    textStyles.dropdownText,
  );

  const style = {
    fontSize: `${fontSize}px`,
    lineHeight: `${fontSize}px`,
  };

  return (
    <div
      ref={container}
      className={classnames(styles.container, {
        [styles.hideControls]: hideControls,
      })}
    >
      <Dropdown.Wrapper
        id={fieldData.id}
        className={styles.wrapper}
        onSelection={handleSelection}
        kind="small"
      >
        <Dropdown.Button className={className} style={style} data-btn>
          {btnText}
        </Dropdown.Button>
        <Dropdown.Menu>
          {fieldData.options && fieldData.options.map((text) => (
            <Dropdown.MenuItem tag="li" key={text} value={text} style={style}>
              {text}
            </Dropdown.MenuItem>
          ))}
        </Dropdown.Menu>
      </Dropdown.Wrapper>

    </div>
  );
}

export default React.forwardRef(DropdownFieldUI);
